
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, CheckCircle, AlertTriangle, Info, AlertOctagon } from 'lucide-react';
import { playSoundEvent, type SoundEvent } from '../../services/sound';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  addToast: (message: string, type?: ToastType, opts?: { sound?: SoundEvent | false }) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: ToastType = 'info', opts?: { sound?: SoundEvent | false }) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
    const soundEvent = opts?.sound === false ? null : (opts?.sound || (type === 'error' ? 'error' : null));
    if (soundEvent) {
      try { playSoundEvent(soundEvent); } catch {}
    }

    // Auto-dismiss
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto min-w-[300px] max-w-sm rounded-lg shadow-lg border p-4 flex items-start gap-3 animate-slide-in-right ${
              toast.type === 'success' ? 'bg-white border-green-200' :
              toast.type === 'error' ? 'bg-white border-red-200' :
              toast.type === 'warning' ? 'bg-white border-yellow-200' :
              'bg-white border-blue-200'
            }`}
          >
            <div className={`mt-0.5 ${
              toast.type === 'success' ? 'text-green-500' :
              toast.type === 'error' ? 'text-red-500' :
              toast.type === 'warning' ? 'text-yellow-500' :
              'text-blue-500'
            }`}>
              {toast.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {toast.type === 'error' && <AlertOctagon className="w-5 h-5" />}
              {toast.type === 'warning' && <AlertTriangle className="w-5 h-5" />}
              {toast.type === 'info' && <Info className="w-5 h-5" />}
            </div>
            <div className="flex-1 text-sm font-medium text-gray-800">
              {toast.message}
            </div>
            <button 
              onClick={() => removeToast(toast.id)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
